import type { FC } from 'react';
import { Button } from '@mui/material';
import { createUseStyles } from 'react-jss';

import type { LcpnTheme } from '../../../styles/theme';
import { Typography } from '../../Typography';
import { CodeBadge } from '../../CodeBadge';
import { getRgb } from '../../PinMap/get-rgb';

interface ConfirmedSummaryCardProps {
  code: string;
  acronym?: string;
  title: string;
  details?: Array<{ label: string; value: string }>;
  headerTitle: string;
  onEdit: () => void;
  onRemove?: () => void;
  compact?: boolean;
  design?: string;
}

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  container: {
    background: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  containerPoi: {
    minHeight: theme.spacing(92),
  },
  containerHeatMap: {
    minHeight: theme.spacing(56),
  },
  header: {
    background: theme.colors['blue-100'],
    minHeight: theme.spacing(10),
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.white,
    padding: theme.spacing(0, 3),
    fontSize: theme.fontSizes.p2,
    justifyContent: 'space-between',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: theme.spacing(3),
  },
  content: {
    backgroundColor: theme.colors.white,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  titleSection: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.colors['grey-100']}`,
  },
  codeLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  acronymText: {
    fontSize: theme.fontSizes.p2,
    fontWeight: 600,
    color: theme.colors.default,
  },
  titleText: {
    fontSize: theme.fontSizes.p2,
    fontWeight: 600,
    color: theme.colors.default,
  },
  detailLabel: {
    color: theme.colors.darker,
    fontWeight: 400,
  },
  detailValue: {
    color: theme.colors.default,
  },
  actions: {
    marginTop: 'auto',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  button: {
    fontSize: theme.fontSizes.p3,
    fontFamily: theme.fontFamilies.default,
    textTransform: 'none',
    textDecoration: 'underline',
  },
}));

export const ConfirmedSummaryCard: FC<ConfirmedSummaryCardProps> = ({
  code,
  acronym,
  title,
  details = [],
  headerTitle,
  onEdit,
  onRemove,
  compact = false,
  design,
}) => {
  const classes = useStyles();

  return (
    <div
      className={`${classes.container} ${
        compact ? classes.containerHeatMap : classes.containerPoi
      }`}
    >
      <div className={classes.header}>
        <p>{headerTitle}</p>
        {onRemove && (
          <Button
            variant="text"
            className={classes.button}
            sx={{ color: 'white' }}
            size="small"
            onClick={onRemove}
          >
            Remove
          </Button>
        )}
      </div>
      <div className={classes.card}>
        <div className={classes.content}>
          <div className={classes.titleSection}>
            <div className={classes.codeLine}>
              <CodeBadge
                code={code}
                backgroundColor={design ? getRgb(design, true) : undefined}
              />
              {acronym && (
                <span className={classes.acronymText}>{acronym}</span>
              )}
            </div>
            <div className={classes.titleText}>{title}</div>
          </div>
          {details.map((detail, index) => (
            <div key={index} style={{ marginBottom: 6 }}>
              <Typography variant="p3" className={classes.detailLabel}>
                {detail.label}:
              </Typography>{' '}
              <Typography variant="p2" className={classes.detailValue}>
                {detail.value}
              </Typography>
            </div>
          ))}
          <div className={classes.actions}>
            <Button
              variant="text"
              className={classes.button}
              size="small"
              onClick={onEdit}
            >
              Edit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
